import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Modal, Input, Table, Button, message, Icon, Spin, Tag, Tooltip, Skeleton } from "antd";

import { batch, checkInstagramExists } from "../actions";

var extractUsernames = function(text) {
  if (!text) return [];

  var usernames = [];

  // First, extract Instagram URLs from HTML href attributes (e.g., href="https://instagram.com/username")
  var hrefMatches = text.match(/href=["']([^"']*instagram\.com\/[^"']+)["']/gi);
  if (hrefMatches) {
    hrefMatches.forEach(function(match) {
      var urlMatch = match.match(/instagram\.com\/([a-zA-Z0-9._]+)/i);
      if (urlMatch) {
        usernames.push(urlMatch[1].toLowerCase());
      }
    });
  }

  // Also extract Instagram URLs directly from text (not in href)
  var urlMatches = text.match(/(?:https?:\/\/)?(?:www\.)?instagram\.com\/([a-zA-Z0-9._]+)/gi);
  if (urlMatches) {
    urlMatches.forEach(function(match) {
      var urlMatch = match.match(/instagram\.com\/([a-zA-Z0-9._]+)/i);
      if (urlMatch) {
        usernames.push(urlMatch[1].toLowerCase());
      }
    });
  }

  // Split by newlines first to process line by line
  var lines = text.split(/\n/);

  lines.forEach(function(line) {
    var trimmed = line.trim();
    if (!trimmed) return;

    // Skip if it looks like HTML tag or already processed URL
    if (trimmed.indexOf('<') !== -1 || trimmed.indexOf('instagram.com') !== -1) return;

    // Skip if line contains spaces (likely a display name like "Max Oliveira")
    if (trimmed.indexOf(' ') !== -1) return;

    // Skip if line contains non-ASCII characters (display names with accents, emojis, etc.)
    // Instagram usernames only allow: a-z, 0-9, dots, underscores
    if (!/^[@a-zA-Z0-9._]+$/.test(trimmed)) return;

    // Remove @ prefix if present
    if (trimmed.startsWith('@')) {
      trimmed = trimmed.substring(1);
    }

    // Skip very short strings (1-2 chars) - likely display name initials, not usernames
    if (trimmed.length < 3) return;

    // Skip if it looks like a capitalized name without numbers/underscores/dots
    // Real usernames typically have: numbers, underscores, dots, or are all lowercase
    // Display names like "Karan", "Janet" are capitalized words without these
    var hasUsernameCharacteristics = /[0-9_.]/.test(trimmed) || trimmed === trimmed.toLowerCase();
    if (!hasUsernameCharacteristics) return;

    // Validate Instagram username format (alphanumeric, dots, underscores, 3-30 chars)
    if (/^[a-zA-Z0-9._]{3,30}$/.test(trimmed)) {
      usernames.push(trimmed.toLowerCase());
    }
  });

  // Remove duplicates
  var unique = [];
  var seen = {};
  usernames.forEach(function(username) {
    if (!seen[username]) {
      seen[username] = true;
      unique.push(username);
    }
  });

  return unique;
};

var InstagramImport = function(props) {
  var visible = props.visible;
  var onCancel = props.onCancel;

  var dispatch = useDispatch();

  var [step, setStep] = useState(0);
  var [inputText, setInputText] = useState('');
  var [usernamesData, setUsernamesData] = useState([]);
  var [loading, setLoading] = useState(false);
  var [checkingDuplicates, setCheckingDuplicates] = useState(false);
  var [importResult, setImportResult] = useState(null);

  var handleTextChange = function(e) {
    setInputText(e.target.value);
  };

  var handleExtract = function() {
    var extracted = extractUsernames(inputText);
    if (extracted.length === 0) {
      message.warning('Nenhum username válido encontrado');
      return;
    }

    // Show loading state and move to step 1
    setCheckingDuplicates(true);
    setStep(1);

    // Call backend API to check existing usernames
    dispatch(checkInstagramExists(extracted))
      .then(function(response) {
        setCheckingDuplicates(false);
        var responseData = response && response.payload && response.payload.data && response.payload.data.data;

        if (responseData && Array.isArray(responseData)) {
          setUsernamesData(responseData);
        } else {
          // Fallback: treat all as new if API fails
          var fallbackData = extracted.map(function(username) {
            return {
              username: username,
              exists: false,
              existingLead: null
            };
          });
          setUsernamesData(fallbackData);
        }
      })
      .catch(function(error) {
        console.error('Error checking duplicates:', error);
        setCheckingDuplicates(false);
        // Fallback: treat all as new if API fails
        var fallbackData = extracted.map(function(username) {
          return {
            username: username,
            exists: false,
            existingLead: null
          };
        });
        setUsernamesData(fallbackData);
      });
  };

  var handleImport = function() {
    var toImport = usernamesData.filter(function(item) { return !item.exists; });

    if (toImport.length === 0) {
      message.warning('Todos os usernames já existem no sistema');
      return;
    }

    setLoading(true);

    var leads = toImport.map(function(item) {
      return {
        name: '@' + item.username,
        instagram: item.username,
        entity_type: 'socialSelling',
        column_id: 1,
        board_id: 1,
      };
    });

    dispatch(batch({ leads: leads }))
      .then(function(response) {
        setLoading(false);
        var responseData = response && response.payload && response.payload.data && response.payload.data.data;
        var created = responseData && responseData.created;
        var failed = responseData && responseData.failed;
        var createdCount = created ? created.length : 0;
        var failedCount = failed ? failed.length : 0;
        var skippedCount = usernamesData.filter(function(item) { return item.exists; }).length;

        setImportResult({
          created: createdCount,
          failed: failedCount,
          skipped: skippedCount,
          failedLeads: failed || [],
          createdLeads: created || []
        });

        setStep(2);
      })
      .catch(function(error) {
        console.error('Import Error:', error);
        message.error('Erro ao importar leads');
        setLoading(false);
      });
  };

  var handleClose = function() {
    setStep(0);
    setInputText('');
    setUsernamesData([]);
    setImportResult(null);
    setCheckingDuplicates(false);
    onCancel();
  };

  var handleBack = function() {
    if (step === 1) {
      setStep(0);
      setUsernamesData([]);
    } else if (step === 2) {
      handleClose();
    }
  };

  var renderStep0 = function() {
    return (
      <div>
        <p style={{ marginBottom: 16, color: '#666' }}>
          Cole abaixo os usernames do Instagram que deseja importar.
          Você pode colar links completos, usernames com @ ou apenas os nomes separados por vírgula, espaço ou quebra de linha.
        </p>
        <Input.TextArea
          value={inputText}
          onChange={handleTextChange}
          placeholder={"@usuario1\n@usuario2\ninstagram.com/usuario3\nusuario4, usuario5"}
          rows={10}
          style={{ fontFamily: 'monospace' }}
        />
        <p style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
          Exemplos aceitos: @username, username, instagram.com/username
        </p>
      </div>
    );
  };

  var renderLoadingSkeleton = function() {
    return (
      <div>
        {/* Summary cards skeleton */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
          <div style={{
            flex: 1,
            padding: '16px 20px',
            background: '#fafafa',
            border: '1px solid #d9d9d9',
            borderRadius: 8,
            textAlign: 'center'
          }}>
            <Skeleton active paragraph={false} title={{ width: 60 }} />
          </div>
          <div style={{
            flex: 1,
            padding: '16px 20px',
            background: '#fafafa',
            border: '1px solid #d9d9d9',
            borderRadius: 8,
            textAlign: 'center'
          }}>
            <Skeleton active paragraph={false} title={{ width: 60 }} />
          </div>
        </div>

        {/* Table skeleton */}
        <div style={{ background: '#fafafa', borderRadius: 6, padding: 16 }}>
          {[1, 2, 3, 4, 5].map(function(i) {
            return (
              <div key={i} style={{ marginBottom: 16 }}>
                <Skeleton active avatar={{ size: 'small', shape: 'circle' }} paragraph={false} title={{ width: '60%' }} />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  var renderStep1 = function() {
    if (checkingDuplicates) {
      return renderLoadingSkeleton();
    }

    var newCount = usernamesData.filter(function(item) { return !item.exists; }).length;
    var existingCount = usernamesData.filter(function(item) { return item.exists; }).length;

    var tableColumns = [
      {
        title: 'Username',
        dataIndex: 'username',
        key: 'username',
        render: function(username, record) {
          return (
            <a
              href={'https://instagram.com/' + username}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: record.exists ? '#999' : '#E1306C' }}
            >
              <Icon type="instagram" style={{ marginRight: 8 }} />
              @{username}
            </a>
          );
        }
      },
      {
        title: 'Status',
        dataIndex: 'exists',
        key: 'status',
        width: 200,
        render: function(exists, record) {
          if (exists) {
            var leadName = record.existingLead && record.existingLead.name ? record.existingLead.name : 'Lead existente';
            return (
              <Tooltip title={'Já existe como: ' + leadName}>
                <Tag color="orange" style={{ margin: 0 }}>
                  <Icon type="warning" style={{ marginRight: 4 }} />
                  Já existe
                </Tag>
              </Tooltip>
            );
          }
          return (
            <Tag color="green" style={{ margin: 0 }}>
              <Icon type="plus" style={{ marginRight: 4 }} />
              Será adicionado
            </Tag>
          );
        }
      }
    ];

    var data = usernamesData.map(function(item, index) {
      return {
        key: index,
        username: item.username,
        exists: item.exists,
        existingLead: item.existingLead
      };
    });

    // Sort: new ones first, existing ones at the end
    data.sort(function(a, b) {
      if (a.exists === b.exists) return 0;
      return a.exists ? 1 : -1;
    });

    return (
      <div>
        {/* Summary cards */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
          <div style={{
            flex: 1,
            padding: '16px 20px',
            background: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: 8,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 28, fontWeight: 600, color: '#52c41a' }}>{newCount}</div>
            <div style={{ color: '#52c41a', fontSize: 13 }}>
              <Icon type="plus-circle" style={{ marginRight: 4 }} />
              Novos leads
            </div>
          </div>
          <div style={{
            flex: 1,
            padding: '16px 20px',
            background: existingCount > 0 ? '#fff7e6' : '#fafafa',
            border: '1px solid ' + (existingCount > 0 ? '#ffd591' : '#d9d9d9'),
            borderRadius: 8,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 28, fontWeight: 600, color: existingCount > 0 ? '#fa8c16' : '#999' }}>{existingCount}</div>
            <div style={{ color: existingCount > 0 ? '#fa8c16' : '#999', fontSize: 13 }}>
              <Icon type="exclamation-circle" style={{ marginRight: 4 }} />
              Já existentes
            </div>
          </div>
        </div>

        <Table
          dataSource={data}
          columns={tableColumns}
          pagination={usernamesData.length > 10 ? { pageSize: 10 } : false}
          size="small"
          scroll={{ y: 280 }}
          rowClassName={function(record) {
            return record.exists ? 'row-disabled' : '';
          }}
        />

        <style>
          {".row-disabled { opacity: 0.6; background: #fafafa; }"}
        </style>
      </div>
    );
  };

  var renderStep2 = function() {
    if (!importResult) return null;

    return (
      <div>
        {/* Result summary cards */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
          <div style={{
            flex: 1,
            padding: '16px 12px',
            background: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: 8,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 24, fontWeight: 600, color: '#52c41a' }}>{importResult.created}</div>
            <div style={{ color: '#52c41a', fontSize: 12 }}>
              <Icon type="check-circle" style={{ marginRight: 4 }} />
              Importados
            </div>
          </div>
          {importResult.skipped > 0 && (
            <div style={{
              flex: 1,
              padding: '16px 12px',
              background: '#fff7e6',
              border: '1px solid #ffd591',
              borderRadius: 8,
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#fa8c16' }}>{importResult.skipped}</div>
              <div style={{ color: '#fa8c16', fontSize: 12 }}>
                <Icon type="minus-circle" style={{ marginRight: 4 }} />
                Ignorados
              </div>
            </div>
          )}
          {importResult.failed > 0 && (
            <div style={{
              flex: 1,
              padding: '16px 12px',
              background: '#fff1f0',
              border: '1px solid #ffa39e',
              borderRadius: 8,
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#f5222d' }}>{importResult.failed}</div>
              <div style={{ color: '#f5222d', fontSize: 12 }}>
                <Icon type="close-circle" style={{ marginRight: 4 }} />
                Falharam
              </div>
            </div>
          )}
        </div>

        {/* Success message */}
        {importResult.created > 0 && (
          <div style={{
            padding: '12px 16px',
            background: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: 6,
            marginBottom: 16
          }}>
            <Icon type="check-circle" theme="filled" style={{ color: '#52c41a', marginRight: 8 }} />
            <strong>{importResult.created}</strong> lead(s) importado(s) com sucesso!
          </div>
        )}

        {/* Skipped info */}
        {importResult.skipped > 0 && (
          <div style={{
            padding: '12px 16px',
            background: '#fffbe6',
            border: '1px solid #ffe58f',
            borderRadius: 6,
            marginBottom: 16
          }}>
            <Icon type="info-circle" theme="filled" style={{ color: '#faad14', marginRight: 8 }} />
            <strong>{importResult.skipped}</strong> lead(s) ignorado(s) pois já existiam no sistema.
          </div>
        )}

        {/* Failed leads table */}
        {importResult.failedLeads.length > 0 && (
          <div>
            <div style={{
              padding: '12px 16px',
              background: '#fff1f0',
              border: '1px solid #ffa39e',
              borderRadius: '6px 6px 0 0',
              borderBottom: 'none'
            }}>
              <Icon type="close-circle" theme="filled" style={{ color: '#f5222d', marginRight: 8 }} />
              <strong>{importResult.failed}</strong> lead(s) não puderam ser importados:
            </div>
            <Table
              dataSource={importResult.failedLeads}
              columns={[
                {
                  title: 'Username',
                  dataIndex: ['data', 'name'],
                  key: 'name',
                  width: 180,
                  render: function(name) {
                    return (
                      <span>
                        <Icon type="instagram" style={{ marginRight: 8, color: '#E1306C' }} />
                        {name}
                      </span>
                    );
                  }
                },
                {
                  title: 'Motivo',
                  dataIndex: 'reason',
                  key: 'reason',
                  render: function(reason) {
                    return <span style={{ color: '#f5222d' }}>{reason}</span>;
                  }
                }
              ]}
              pagination={false}
              size="small"
              rowKey={function(record) { return record.index; }}
              style={{ border: '1px solid #ffa39e', borderRadius: '0 0 6px 6px' }}
            />
          </div>
        )}
      </div>
    );
  };

  var getTitle = function() {
    if (step === 0) return 'Importar do Instagram';
    if (step === 1) return checkingDuplicates ? 'Verificando...' : 'Confirmar Importação';
    if (step === 2) return 'Resultado da Importação';
    return 'Importar do Instagram';
  };

  var getOkText = function() {
    if (step === 0) return 'Verificar Usernames';
    if (step === 1) {
      if (checkingDuplicates) return 'Verificando...';
      var newCount = usernamesData.filter(function(item) { return !item.exists; }).length;
      return 'Importar ' + newCount + ' Lead(s)';
    }
    if (step === 2) return 'Fechar';
    return 'OK';
  };

  var handleOk = function() {
    if (step === 0) {
      handleExtract();
    } else if (step === 1) {
      handleImport();
    } else if (step === 2) {
      handleClose();
    }
  };

  var newCount = usernamesData.filter(function(item) { return !item.exists; }).length;

  return (
    <Modal
      visible={visible}
      title={
        <span>
          <Icon type="instagram" style={{ marginRight: 8, color: '#E1306C' }} />
          {getTitle()}
        </span>
      }
      onCancel={handleClose}
      width={600}
      footer={[
        step > 0 && step < 2 && !checkingDuplicates && (
          <Button key="back" onClick={handleBack}>
            Voltar
          </Button>
        ),
        <Button key="cancel" onClick={handleClose}>
          {step === 2 ? 'Fechar' : 'Cancelar'}
        </Button>,
        step < 2 && (
          <Button
            key="ok"
            type="primary"
            onClick={handleOk}
            loading={loading || checkingDuplicates}
            disabled={(step === 0 && !inputText.trim()) || (step === 1 && (checkingDuplicates || newCount === 0))}
          >
            {getOkText()}
          </Button>
        )
      ].filter(Boolean)}
    >
      <Spin spinning={loading}>
        {step === 0 && renderStep0()}
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
      </Spin>
    </Modal>
  );
};

export default InstagramImport;
